import * as THREE from 'three/webgpu';

import { SpriteBatch } from './Billboards.js';
import { RENDER_ORDER } from './materials.js';

/**
 * @file `SpeechBubbleBatch` — the little rounded card that pops up over an NPC's
 * head and TYPES its line out one character at a time. `systems/Speech.js` owns
 * the model (who is talking, what they say, how far the typewriter has got);
 * this file owns the pixels and nothing else.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE CANVAS ATLAS THROUGH ONE `SpriteBatch` — NOT N QUADS, NOT A DOM OVERLAY
 * ─────────────────────────────────────────────────────────────────────────────
 * The canvas is {@link BUBBLE.cellW} x ({@link BUBBLE.cellH} * `cells`): a
 * single COLUMN of stacked cells, one per simultaneously-live bubble. Every
 * bubble on screen is therefore one instance of ONE `SpriteBatch` with
 * `frame = its cell index`, which is ONE DRAW CALL for the whole conversation,
 * on both backends, with zero new shader code.
 *
 * The two alternatives were both worse:
 *
 *   • **A textured quad per bubble.** N draws, N materials, N textures, and each
 *     one needs its own camera-facing vertex path — a reimplementation of
 *     `Billboards.js` with none of its scar tissue (the `varying()` frame decode,
 *     the `abs(iScale.x)` mirror, the two-backend split, the depth bias).
 *   • **A DOM overlay projected from world space.** It is the cheapest to write
 *     and it is the one thing this project has no room for: HTML text does not
 *     sit in the world's light, does not fog, does not sort against the geometry,
 *     and slides against the frame whenever the renderer and the DOM disagree by
 *     a frame. A bubble that floats over the buildings is HUD chrome; this is
 *     meant to be a thing in the street.
 *
 * A live bubble OWNS its cell for its whole life (the model hands us a stable
 * `cell` index), so the only time a cell is repainted is when what it says — or
 * how faded it is — actually changes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE REPAINT / UPLOAD ACCOUNTING, WHICH IS THE WHOLE PERFORMANCE STORY
 * ─────────────────────────────────────────────────────────────────────────────
 * three re-uploads the WHOLE canvas whenever `texture.needsUpdate` is set. There
 * is no partial-cell upload: repainting one 448x112 cell instead of all five
 * saves CPU rasterisation, and saves NOTHING on bandwidth. At the shipped size
 * the atlas is 448 x 560 RGBA = **1,003,520 bytes, ~1.0 MB per upload**.
 *
 * That is cheap at the real rate and ruinous at a naive one. Real rate: a line
 * types for about a second of a roughly four-second life, so it changes on maybe
 * a third of the frames it is alive for, and a HELD line — the two seconds it
 * sits there fully typed — repaints NOT AT ALL. Naive rate would be 1 MB every
 * frame for a bubble that is not doing anything, i.e. ~60 MB/s of PCIe to
 * animate nothing.
 *
 * So every cell carries a SIGNATURE (the full line, the revealed count, the
 * quantised alpha, and the style's three colour strings). {@link add} repaints
 * only when the signature moves, and {@link commit} sets `needsUpdate` AT MOST
 * ONCE per frame and only if at least one cell was actually repainted.
 * {@link stats} counts `repaints` and `uploads` so this cannot rot silently —
 * if `uploads` ever approaches the frame count, something upstream is jittering
 * a signature.
 *
 * ⚠ **THE FADE LIVES IN THE CANVAS, NOT IN A TINT.** `SpriteBatch` has no
 * per-instance opacity; it has `iTintD`, and with `nightMix: false` the shader is
 * `iTintD * warmGrade()` MULTIPLIED BY THE TEXTURE'S ALPHA. Scaling `iTintD`
 * therefore DARKENS a bubble toward black — it does not fade it out — so a
 * fading-out line would turn into a black card before it disappeared. The alpha
 * multiply has to happen where the alpha channel is written: `ctx.globalAlpha`
 * at paint time. The consequence is that a change in `alpha` is a REPAINT, and
 * an un-quantised fade would repaint (and upload 1 MB) every single frame of it.
 * Hence {@link ALPHA_STEPS}: alpha is snapped to 1/16, so a full fade costs at
 * most 16 repaints however long it takes. The PAINTED value is the quantised one
 * too, not the caller's raw one — painting the raw value while comparing the
 * quantised one would make the picture depend on which frame happened to trip
 * the repaint.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BLEED: `signText.js`'s RULE, WITH THE TRANSPARENCY TWIST
 * ─────────────────────────────────────────────────────────────────────────────
 * Mipmaps are REQUIRED here for the reason `assets/signText.js` argues at
 * length: text is the one thing in this project that MUST mipmap, or the
 * letterforms crawl as the camera moves. But a mip level bleeds neighbouring
 * cells together, and where `signText`'s atlas is OPAQUE BOARDS — so the bleed
 * pulls in a neighbour's background colour through a gutter painted that same
 * colour, i.e. it is invisible — ours is TRANSPARENT, so a bleed pulls in the
 * neighbour's LETTERS. Ghost words floating beside a bubble.
 *
 * Two mitigations, and both are load-bearing:
 *
 *   1. **One column.** `cols: 1`, cells stacked vertically, so no cell has a
 *      HORIZONTAL neighbour at all and `ClampToEdgeWrapping` covers the two ends
 *      of the strip.
 *   2. **{@link BUBBLE.gutter} px of FULLY TRANSPARENT margin** at the top and
 *      bottom of every cell that the bubble body and its tail never enter. A mip
 *      level that reaches across the cell edge finds transparent pixels, which
 *      is the transparent atlas's equivalent of signText's own-background border.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO SMALLER DECISIONS THAT LOOK LIKE BUGS IF YOU CHANGE THEM
 * ─────────────────────────────────────────────────────────────────────────────
 * • **`nightMix: true`, WITH A TINT PAIR THE BLOOM THRESHOLD DICTATES — and it
 *   shipped as `false` first, which was wrong on arithmetic nobody ran.** The
 *   original argument was that a speech bubble is a LEGIBILITY surface and
 *   blending it toward the night tint would make the text unreadable at midnight.
 *   The premise is false: `SpriteBatch`'s tint MULTIPLIES the whole sampled
 *   texel, board AND ink alike, so a uniform tint moves the absolute level and
 *   leaves the CONTRAST RATIO exactly where it was. Nothing becomes unreadable;
 *   it only becomes dimmer, which at midnight is what "in the world" means.
 *
 *   What `false` actually bought was a bubble that BLOOMED. `dialogue.js`'s board
 *   `#f4efe6` is **0.905, 0.863, 0.791** in linear, and `warmGrade()` peaks at
 *   1.16 on red — so the untinted day channel reaches **1.05** against
 *   `post.js`'s `bloomThreshold` **0.80**, and 0.905 against the
 *   `bloomThresholdNight` **0.45**. The bubble glowed in daylight and haloed at
 *   midnight, and **bloom on TEXT smears the letterforms** — the one thing this
 *   whole file is arranged to protect. Found in `/play/speech`, not by a test.
 *
 *   {@link BUBBLE.tintDay} / {@link BUBBLE.tintNight} are therefore DERIVED from
 *   those two thresholds, not chosen: worst channel × warm × tint must clear the
 *   threshold at EVERY point of the crossfade, since BOTH sides move — the value
 *   lerps `tintDay`→`tintNight` on night and the threshold lerps 0.80→0.45 on the
 *   same night.
 *
 *   ⚠ AN EARLIER VERSION OF THIS PARAGRAPH CLAIMED THE TIGHTEST MARGIN IS "NOT AT
 *   EITHER END", AND IT IS WRONG — it was contradicted by its own next sentence,
 *   which named noon. Hold `uWarm` at `warmGrade`'s peak (the conservative
 *   ceiling, and what `tests/speech.test.mjs` section (m) does) and both sides are
 *   LINEAR in night, so the margin is linear and its extremes are exactly the two
 *   ENDS. That is what makes an endpoint check a PROOF rather than a spot check —
 *   worth knowing before somebody "strengthens" it into a sampled sweep and
 *   concludes the sampling is what makes it safe. Solved: **0.777/0.800 at noon**
 *   — the tightest, using 97% of its allowance, so the board is as bright as
 *   bloom permits and no brighter — 0.569/0.625 at dusk, 0.372/0.450 at midnight.
 *
 *   ⚠ Move the board colour in `src/game/dialogue.js` and these two constants are
 *   re-derived — that cross-file dependency is why `tests/speech.test.mjs` pins
 *   the invariant against the REAL board, the REAL `WARM_TINT` and the REAL
 *   `post.js` thresholds rather than against these numbers, and sweeps all 21
 *   colours (bg/fg/accent × 7 topics) rather than the board alone.
 *
 *   It still goes through `warmGrade()` and `weatherGrade()` inside the shader,
 *   so it takes the world's golden hour and its storms and sits IN the scene
 *   rather than floating over it like HUD chrome.
 *
 * • **The text is LEFT-ALIGNED, and this is the single most important detail in
 *   the file.** A centred typewriter re-centres on every new character, so every
 *   already-revealed letter jumps LEFT one half-glyph each tick. It does not read
 *   as typing, it reads as broken text. The bubble BODY is likewise sized to the
 *   FULL line and not to the revealed prefix, so the card does not grow while it
 *   types — a growing box is the other half of the same illusion breaking.
 *
 * @module engine/render/SpeechBubbles
 */

/** @typedef {import('../types.js').EngineCaps} EngineCaps */
/** @typedef {import('../types.js').Registry} Registry */
/** @typedef {{bg: string, fg: string, accent: string}} BubbleStyle */

/**
 * Quantisation of the fade, in steps over 0..1. See the header: alpha is painted
 * into the canvas, so every distinct alpha is a repaint and a 1 MB upload. 16
 * steps is invisible on a ~0.25 s fade (a step every ~16 ms, one frame) and caps
 * a fade of ANY duration at 16 repaints. Exported so the model's fade curve and
 * the tests can talk about the same number.
 */
export const ALPHA_STEPS = 16;

/**
 * Atlas and quad geometry. Pixels and metres.
 *
 * The pixel numbers are a texture budget and the metre numbers are how big the
 * thing is in the street; the two meet at the ASPECT, which is derived from the
 * cell and never authored twice.
 */
export const BUBBLE = Object.freeze({
  /** Cell width, px. 448 leaves ~410 px of interior for a line the model caps
   *  at 26 characters — comfortable at {@link BUBBLE.fontPx} without condensing. */
  cellW: 448,
  /** Cell height, px. 4:1 with the width, and the QUAD'S ASPECT IS DERIVED FROM
   *  IT (`cellW / cellH`) rather than sniffed off the canvas. */
  cellH: 112,
  /**
   * Quad height in metres; with the 4:1 cell the card is `worldH * 4` wide.
   *
   * ⚠ **THIS IS THE ONLY LEVER ON TEXT SIZE, AND IT SHIPPED AT HALF THIS —
   * 0.52 — WHICH WAS UNREADABLE IN THE GAME.** Work it out before changing it:
   * `core/Engine.js` frames at `distance` 26 m and `fov` 30, so the screen spans
   * `2 * 26 * tan(hfov/2)` ≈ **24.8 m** at 16:9. The old 2.08 m card was 8.4% of
   * that — **161 px for a 26-character line, i.e. 6.2 px PER CHARACTER**, which
   * is not text, it is a texture of text. At 1.04 the same line gets **~12.4
   * px/char** and is comfortably readable. Owner call, from playing it.
   *
   * ⚠ **RAISING THE FONT INSTEAD DOES NOT WORK, and this is the part to keep.**
   * A 26-char line at {@link BUBBLE.fontPx} already measures ~390 px inside 448
   * of cell — the card is ~95% text and there is no padding left to reclaim, so
   * a bigger `fontPx` alone just trips `drawBubble`'s condense and returns
   * narrower letters at the same width. Text size and card size are ONE number
   * here, and it is this one.
   *
   * ⚠ It costs no sharpness and no memory: the atlas is unchanged, and a 448 px
   * cell drawn at ~322 px is still MINIFYING (1.4x), so the letterforms are
   * cleaner than before rather than upscaled. There is headroom to roughly 4x
   * the original before a cell would be magnified — the real ceiling is the
   * WIDTH of a worst-case line on screen (4.16 m is ~17% of the view; 3x would
   * be a quarter of it, and two speakers would overlap constantly).
   */
  worldH: 1.04,
  /** Interior padding, px, between the text and the bubble's border on all sides. */
  pad: 18,
  /** Corner radius, px. Generous: a soft card reads as speech, a sharp one as UI. */
  radius: 22,
  /** Border stroke width, px. Drawn in `style.accent`, which is what separates a
   *  pale bubble from a pale wall behind it. */
  border: 3,
  /** Width of the tail's base, px. */
  tailW: 20,
  /** Height of the downward spike that points at the speaker, px. Its TIP is the
   *  bottom of the bubble body's footprint (bar the gutter) — see the note on
   *  `anchorY` in the constructor. */
  tailH: 14,
  /** Text size, px. Must agree with the size inside {@link BUBBLE.font}. */
  fontPx: 30,
  /** Full CSS font shorthand. ⚠ Keep the px in here in sync with `fontPx` —
   *  `fontPx` is what the caret and the vertical metrics are derived from. */
  font: '600 30px "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  /** Px of FULLY TRANSPARENT margin at the top and bottom of every cell. The
   *  anti-bleed rule for a transparent atlas — see the header. */
  gutter: 6,
  /** Draw a block caret while the line is still typing. It is what makes the
   *  effect read as typing rather than as a texture that failed to stream in. */
  caret: true,
  /**
   * Day tint, multiplied over the whole sampled texel (board AND ink, so the
   * contrast ratio is untouched — see the header). DERIVED, not chosen: the
   * board's worst linear channel 0.905 times `warmGrade`'s 1.16 red peak times
   * this must stay under `post.js#bloomThreshold` 0.80. 0.74 lands at 0.777.
   * Raise it and the bubble glows in daylight and its letterforms smear.
   */
  tintDay: /** @type {[number, number, number]} */ ([0.74, 0.74, 0.74]),
  /**
   * Night tint. Same derivation against `bloomThresholdNight` 0.45: 0.905 × 0.40
   * = 0.362. Deliberately COOL rather than neutral — a warm-grey board in a
   * blue-lit street reads as a sticker pasted over the scene, and the night
   * channel is the one term `warmGrade` never touches.
   */
  tintNight: /** @type {[number, number, number]} */ ([0.40, 0.42, 0.47]),
});

/** Caret geometry, as fractions of {@link BUBBLE.fontPx}. */
const CARET = Object.freeze({
  /** Width of the block. */
  w: 0.14,
  /** Height, roughly a cap height so it sits with the letters. */
  h: 0.74,
  /** Gap after the last revealed glyph. */
  gap: 0.08,
});

export class SpeechBubbleBatch {
  /**
   * @param {Object} o
   * @param {number} [o.cells=5] Atlas cells == the model's `maxLive`. This is the
   *   instance cap AND the texture height, so it is not free: 5 cells is 1.0 MB.
   * @param {EngineCaps} [o.caps] Engine caps. Forwarded to `SpriteBatch` (it
   *   reads `instancedSprite` to pick its backend) AND the default source of
   *   {@link SpeechBubbleBatch#constructor}'s `anisotropy` — see below.
   * @param {THREE.Object3D} [o.parent] Adds itself when given. ⚠ IDENTITY
   *   TRANSFORM ONLY — `SpriteBatch` treats instance positions as WORLD space and
   *   its two backends disagree under a transformed parent.
   * @param {Registry|null} [o.registry] Tracked for `Level.dispose()`.
   * @param {number} [o.anisotropy] Defaults to `caps.maxAnisotropy`, so passing
   *   `caps` is all a caller has to do. Text read at the game's 52-degree pitch
   *   is the biggest quality win going, same as the sign atlas.
   *
   *   ⚠ THIS JSDOC USED TO SAY "from `renderer.capabilities.getMaxAnisotropy()`"
   *   AND THAT IS WRONG ON THIS BACKEND — `WebGPURenderer` exposes
   *   `getMaxAnisotropy()` DIRECTLY and has no `.capabilities` at all, so the
   *   recommended expression optional-chains to `undefined` and silently falls
   *   back to 1. `/play/speech` copied it verbatim and spent a round asking
   *   reviewers to judge anisotropic filtering that was switched off; the whole
   *   point of a default is that nobody has to reach for it. `core/renderer.js`
   *   resolves it once into `caps.maxAnisotropy` — that field is THE source.
   * @param {string} [o.name='speechBubbles']
   */
  constructor(o = {}) {
    const {
      cells = 5,
      caps = null,
      parent = null,
      registry = null,
      // Defaults off `caps` — declared AFTER it, so the binding is in scope.
      // An explicit 0/undefined still lands on 1 at the clamp below.
      anisotropy = caps?.maxAnisotropy ?? 1,
      name = 'speechBubbles',
    } = o;

    if (typeof document === 'undefined') {
      throw new Error('SpeechBubbleBatch: needs a DOM canvas — this class cannot be constructed in node');
    }

    /** @type {number} Cells, instances and atlas rows: one number, three jobs. */
    this.cells = Math.max(1, cells | 0);
    /** @type {string} */
    this.name = name;
    /** @type {number} Instances written since the last {@link reset}. */
    this.count = 0;

    this._repaints = 0;
    this._uploads = 0;
    /**
     * Reused `SpriteBatch#set` options. The two tints never vary per instance —
     * they are the bloom-derived constants — so only `frame` is rewritten.
     * @type {{frame: number, tintD: [number, number, number], tintN: [number, number, number]}}
     */
    this._setOpts = { frame: 0, tintD: BUBBLE.tintDay, tintN: BUBBLE.tintNight };
    this._overflow = 0;
    this._dirty = false;
    this._disposed = false;

    /**
     * Per-cell signature of what is currently PAINTED there. `null` = never
     * painted. See the header: this is the whole repaint gate.
     * @type {Array<string|null>}
     */
    this._sig = new Array(this.cells).fill(null);

    // ── the atlas ────────────────────────────────────────────────────────────
    const canvas = document.createElement('canvas');
    canvas.width = BUBBLE.cellW;
    canvas.height = BUBBLE.cellH * this.cells;
    /** @type {HTMLCanvasElement} */
    this._canvas = canvas;
    /** @type {CanvasRenderingContext2D} */
    this._ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));

    /**
     * A one-cell scratch. Every cell is painted here at FULL alpha and then
     * blitted into the atlas through `globalAlpha`, so a faded bubble fades as a
     * finished IMAGE. Painting the parts at `globalAlpha` directly would composite
     * the border against the already-faded fill and give a bubble whose outline
     * changes colour as it fades.
     */
    const scratch = document.createElement('canvas');
    scratch.width = BUBBLE.cellW;
    scratch.height = BUBBLE.cellH;
    this._scratch = scratch;
    this._sctx = /** @type {CanvasRenderingContext2D} */ (scratch.getContext('2d'));

    const tex = new THREE.CanvasTexture(canvas);
    tex.name = name;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = Math.max(1, Math.round(anisotropy));
    tex.needsUpdate = true;
    /** @type {THREE.CanvasTexture} */
    this.texture = tex;
    registry?.track?.(tex);

    // ── the batch ────────────────────────────────────────────────────────────
    /** @type {SpriteBatch} */
    this.batch = new SpriteBatch({
      texture: tex,
      // One COLUMN: no horizontal neighbour to bleed into. See the header.
      cols: 1,
      rows: this.cells,
      max: this.cells,
      caps,
      worldHeight: BUBBLE.worldH,
      // ⚠ EXPLICIT, never sniffed off the image. We size the canvas ourselves so
      // this is a constant; letting `SpriteBatch` measure it makes the quad's
      // shape a silent dependency on when the canvas happened to be decoded.
      aspect: BUBBLE.cellW / BUBBLE.cellH,
      // 0 = the quad stands ON the anchor, so it grows UPWARD from the tail tip.
      anchorY: 0,
      // Genuinely blended art (soft corners, a fade), so: no cutout, no depth
      // write, and an explicit render order below.
      alphaTest: 0,
      transparent: true,
      depthWrite: false,
      // ⚠ TRUE, and the tint pair is derived from the bloom thresholds — see the
      // header. `false` (how this shipped first) makes the board bloom and smear
      // its own letters; the tint multiplies board and ink alike, so dimming it
      // costs no contrast at all.
      nightMix: true,
      sizeAttenuation: true,
      ownsTexture: false,
      name,
    });

    // `SpriteBatch` sets no render order at all, so a blended quad would fall in
    // with the rest of the transparent pass and sort by bounding-sphere z — the
    // strobe the RENDER_ORDER table exists to prevent.
    this.batch.object.renderOrder = RENDER_ORDER.speech;

    /** @type {THREE.Object3D} */
    this.object = this.batch.object;
    if (parent) parent.add(this.object);
  }

  /** Starts a frame. Anything not re-added before {@link commit} stops drawing. */
  reset() {
    this.count = 0;
    this.batch.clear();
    return this;
  }

  /**
   * One bubble. Repaints its cell ONLY if the revealed text, the fade or the
   * style changed — see the header for why that gate is the whole design.
   *
   * @param {number} x @param {number} y @param {number} z World position of the
   *   TAIL TIP; the quad grows UPWARD from here (`anchorY: 0`). ⚠ The tail tip
   *   sits `BUBBLE.gutter + BUBBLE.border / 2` px above the cell's bottom edge —
   *   **7.5 px, i.e. ~7.0 cm of the quad's 1.04 m**, not `gutter` alone: the tail
   *   is STROKED, so the path stops half a stroke short or the border's outer
   *   edge would land outside the anti-bleed margin it exists to respect. ⚠ This
   *   JSDoc said `gutter` alone (~5.6 cm) and was wrong by 7 mm against
   *   `drawBubble`'s own `B.cellH - B.gutter - b / 2`; the PAINTER is the source.
   *   It is a FRACTION of `worldH`, so it scales with any size change and the
   *   figure has to be recomputed with one. Aim the caller a hair
   *   below the head rather than compensating here; the margin is not negotiable.
   * @param {string} text The FULL line. The body is sized to this, not to the
   *   revealed prefix, so the card does not grow while it types.
   * @param {number} chars How many characters are revealed (the typewriter).
   *   Clamped into `0..text.length`.
   * @param {number} alpha 0..1. Quantised to {@link ALPHA_STEPS} before it is
   *   compared OR painted.
   * @param {number} cell Stable atlas cell index, `0..cells-1`. A live bubble
   *   owns its cell for its whole life; the model is what guarantees that.
   * @param {BubbleStyle} style CSS colour strings — this file never touches the
   *   project's linear palette, exactly like `assets/signText.js`.
   * @returns {boolean} false when the batch is full or the cell is out of range
   *   (counted in {@link stats}, never grown).
   */
  add(x, y, z, text, chars, alpha, cell, style) {
    if (this._disposed) return false;
    const c = cell | 0;
    if (this.count >= this.cells || c < 0 || c >= this.cells) {
      if (this._overflow++ === 0) {
        console.warn(`[SpeechBubbleBatch:${this.name}] cell ${cell} outside 0..${this.cells - 1} or batch full at ${this.cells}; bubble dropped`);
      }
      return false;
    }

    const line = text ?? '';
    const n = Math.max(0, Math.min(line.length, Math.round(chars ?? line.length)));
    // Quantise ONCE, then use the same number to compare and to paint.
    const a = quantAlpha(alpha);
    const st = style ?? { bg: '#fff', fg: '#000', accent: '#000' };

    // The signature carries the FULL LINE, not just its length: a cell is reused
    // by the next speaker, and two different 14-character lines are exactly the
    // collision that would leave the previous NPC's words on screen.
    const sig = `${n}|${a}|${st.bg}|${st.fg}|${st.accent}|${line}`;
    if (this._sig[c] !== sig) {
      this._paintCell(c, line, n, a, st);
      this._sig[c] = sig;
      this._repaints++;
      this._dirty = true;
    }

    // The tints are per-batch constants, but `SpriteBatch` has no per-batch tint
    // slot — every instance carries its own pair. Written from the frozen table
    // so a caller can never disagree with the bloom arithmetic behind it. The
    // options object is REUSED: `set` copies every field out before returning.
    this._setOpts.frame = c;
    this.batch.set(this.count++, x, y, z, this._setOpts);
    return true;
  }

  /** Uploads whatever changed and syncs the draw count. Once per frame. */
  commit() {
    if (this._disposed) return;
    if (this._dirty) {
      // ONE flag for the whole atlas, however many cells moved — see the header:
      // three has no partial upload, so a second flag would be a second 1 MB.
      this.texture.needsUpdate = true;
      this._uploads++;
      this._dirty = false;
    }
    this.batch.commit();
  }

  /**
   * Forwards to the underlying `SpriteBatch` — `Gameplay`'s `syncDepthBias`
   * calls it with {@link import('./Billboards.js').cameraDepthBias}'s vector, the
   * same one the actor batches take, so a bubble lifts out of the wall behind its
   * speaker by the same rule the speaker does.
   * @param {number} [x] @param {number} [y] @param {number} [z]
   */
  setDepthBias(x = 0, y = 0, z = 0) {
    this.batch.setDepthBias(x, y, z);
  }

  /** @returns {{count: number, cells: number, repaints: number, uploads: number, dropped: number}} */
  stats() {
    return {
      count: this.count,
      cells: this.cells,
      repaints: this._repaints,
      uploads: this._uploads,
      dropped: this._overflow,
    };
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object?.removeFromParent?.();
    // Owns its material and geometry; `ownsTexture: false`, so the texture is
    // ours to free.
    this.batch.dispose();
    this.texture.dispose();
    this._sig.length = 0;
  }

  // ── painting ───────────────────────────────────────────────────────────────

  /**
   * Paints one cell: at full alpha into the scratch canvas, then blitted into the
   * atlas through `globalAlpha`. See `_scratch` for why the two stages.
   *
   * @param {number} cell
   * @param {string} line Full text.
   * @param {number} chars Revealed count, already clamped.
   * @param {number} alpha Already quantised.
   * @param {BubbleStyle} style
   */
  _paintCell(cell, line, chars, alpha, style) {
    const g = this._sctx;
    // ⚠ CLEAR FIRST, the whole cell. A shorter line painted over a longer one
    // leaves the tail of the old sentence behind, and with `transparent: true`
    // there is no opaque background to hide it.
    g.clearRect(0, 0, BUBBLE.cellW, BUBBLE.cellH);
    if (alpha > 0 && line) drawBubble(g, line, chars, style);

    const dst = this._ctx;
    const top = cell * BUBBLE.cellH;
    dst.clearRect(0, top, BUBBLE.cellW, BUBBLE.cellH);
    dst.save();
    dst.globalAlpha = alpha;
    dst.drawImage(this._scratch, 0, top);
    dst.restore();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawing — cell-local coordinates, full alpha, no knowledge of the atlas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The whole card, in cell-local pixels with the origin at the cell's top-left.
 *
 * Order: body + tail as ONE PATH -> fill -> stroke -> text (+ caret). The single
 * path matters: stroking a rounded rect and a triangle separately draws the
 * border ACROSS the body's bottom edge where the tail joins it, and the seam is
 * plainly visible at 30 px of text.
 *
 * @param {CanvasRenderingContext2D} g
 * @param {string} line Full text — the body is sized to this.
 * @param {number} chars Revealed count.
 * @param {BubbleStyle} style
 */
function drawBubble(g, line, chars, style) {
  const B = BUBBLE;
  const cx = B.cellW / 2;
  const b = B.border;

  g.save();
  g.font = B.font;
  g.textBaseline = 'middle';
  g.textAlign = 'left';

  // Measure the FULL line: the card must be its final size from the first
  // character, or it grows as it types and reads as a layout bug.
  const textW = g.measureText(line).width || 1;
  const maxBody = B.cellW - 2 * B.gutter;
  const minBody = B.tailW + 2 * B.radius + 2 * b;
  const bodyW = Math.max(minBody, Math.min(maxBody, textW + 2 * B.pad));
  // Backstop only: the model rejects lines over 26 characters, so this should
  // never bite. Condensing beats clipping when it does — `signText.js#drawBoard`
  // makes the same call for a long shop name.
  const squeeze = Math.min(1, (bodyW - 2 * B.pad) / textW);

  const left = cx - bodyW / 2 + b / 2;
  const right = cx + bodyW / 2 - b / 2;
  const top = B.gutter + b / 2;
  // The body stops `tailH` short of the bottom gutter; the tail fills that band.
  const bottom = B.cellH - B.gutter - B.tailH - b / 2;
  const r = Math.min(B.radius, (right - left) / 2, (bottom - top) / 2);

  bubblePath(g, left, top, right, bottom, r, cx, B.cellH - B.gutter - b / 2, B.tailW);
  g.fillStyle = style.bg;
  g.fill();
  g.lineWidth = b;
  g.lineJoin = 'round';
  g.strokeStyle = style.accent;
  g.stroke();

  // ⚠ LEFT-ALIGNED, and this is the one line in the file not to "improve". A
  // centred typewriter shifts every already-revealed glyph left on each new
  // character; it reads as broken text, not as typing.
  const revealed = line.slice(0, chars);
  const midY = (top + bottom) / 2;
  g.translate(left + b / 2 + B.pad, midY);
  g.scale(squeeze, 1);
  g.fillStyle = style.fg;
  if (revealed) g.fillText(revealed, 0, 0);

  if (B.caret && chars < line.length) {
    // measureText ignores the transform, so this is in the same pre-squeeze
    // space the text was laid out in.
    const cw = B.fontPx * CARET.w;
    const ch = B.fontPx * CARET.h;
    const cxx = (revealed ? g.measureText(revealed).width : 0) + B.fontPx * CARET.gap;
    g.fillRect(cxx, -ch / 2, cw, ch);
  }

  g.restore();
}

/**
 * The rounded rect and its downward spike as one closed path, walked clockwise
 * from the top-left corner. The tail interrupts the BOTTOM edge, so the stroke
 * runs round the outside of both shapes and there is no seam where they meet.
 *
 * @param {CanvasRenderingContext2D} g
 * @param {number} left @param {number} top @param {number} right @param {number} bottom
 * @param {number} r Corner radius, already clamped.
 * @param {number} tipX @param {number} tipY Where the tail points — the speaker.
 * @param {number} tailW Base width of the tail on the bottom edge.
 */
function bubblePath(g, left, top, right, bottom, r, tipX, tipY, tailW) {
  // Keep the tail's base inside the two bottom corners whatever the body width.
  const half = Math.min(tailW / 2, (right - left) / 2 - r);
  const tl = tipX - half;
  const tr = tipX + half;

  g.beginPath();
  g.moveTo(left + r, top);
  g.lineTo(right - r, top);
  g.quadraticCurveTo(right, top, right, top + r);
  g.lineTo(right, bottom - r);
  g.quadraticCurveTo(right, bottom, right - r, bottom);
  g.lineTo(tr, bottom);
  g.lineTo(tipX, tipY);
  g.lineTo(tl, bottom);
  g.lineTo(left + r, bottom);
  g.quadraticCurveTo(left, bottom, left, bottom - r);
  g.lineTo(left, top + r);
  g.quadraticCurveTo(left, top, left + r, top);
  g.closePath();
}

/**
 * Snap a fade to {@link ALPHA_STEPS}. Exported reasoning lives in the header;
 * the property that matters here is that 1 and 0 survive EXACTLY, so a fully
 * opaque bubble is never painted at 0.9375 and a dead one is never a ghost.
 *
 * @param {number} a
 * @returns {number} 0..1, one of `ALPHA_STEPS + 1` values.
 */
function quantAlpha(a) {
  const v = a == null ? 1 : a;
  if (!(v > 0)) return 0;
  if (v >= 1) return 1;
  return Math.round(v * ALPHA_STEPS) / ALPHA_STEPS;
}
